<script lang="ts">
/** One header cell. `key` is the sort key the caller sends to the backend. */
export interface GaiaDataColumn {
  key: string;
  label: string;
  /** CSS grid track — `160px`, `minmax(180px,1.1fr)`. */
  width: string;
  sortable?: boolean;
  /**
   * Where the header sits in its track. `right` is the File Detail page·status
   * column; `center` is for a track holding a single mark rather than a value —
   * the vault's owner avatar, signed tick and status pill — where a left-set
   * header would float away from what it labels.
   */
  align?: 'left' | 'center' | 'right';
}
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { gaiaFontSize, gaiaFontWeight, gaiaNeutral, gaiaTracking } from '../themes/tokens';

const props = defineProps({
  columns: {
    type: Array as PropType<GaiaDataColumn[]>,
    required: true,
  },
  /** Column currently sorted. Nothing is drawn until the user sorts. */
  sortKey: {
    type: String,
    default: undefined,
  },
  sortDirection: {
    type: String as PropType<'asc' | 'desc'>,
    default: 'desc',
  },
  /**
   * The user has actually clicked a header. A list arrives sorted by
   * `created desc` — showing a caret for that would claim a choice nobody made.
   */
  sortActive: {
    type: Boolean,
    default: false,
  },
  /** A leading track for a per-row control (merge checkbox, row selection). */
  leadColumn: {
    type: Boolean,
    default: false,
  },
  /** Width of that leading track. The merge checkbox wants 26; a selection
      column with a select-all box in the header wants the mock's 40. */
  leadWidth: {
    type: Number,
    default: 26,
  },
  /** Trailing track for the per-row actions; 0 drops it. */
  actionWidth: {
    type: Number,
    default: 72,
  },
});

const emit = defineEmits<{ sort: [key: string] }>();

const gap = 16;
const framePadding = 36;

/** The row template every header, body and skeleton row is laid out on. */
const grid = computed(() =>
  [
    props.leadColumn ? `${props.leadWidth}px` : '',
    ...props.columns.map(column => column.width),
    props.actionWidth ? `${props.actionWidth}px` : '',
  ]
    .filter(Boolean)
    .join(' ')
);

/** Narrowest each track can get — `minmax(180px,…)` gives 180, `92px` gives 92. */
function trackMin(width: string): number {
  const flexible = /minmax\(\s*([\d.]+)px/.exec(width);
  if (flexible) return Number.parseFloat(flexible[1]);
  const fixed = /([\d.]+)px/.exec(width);
  return fixed ? Number.parseFloat(fixed[1]) : 120;
}

/**
 * Below this the card scrolls sideways rather than crushing the columns. Same
 * sum the mock does: every track's floor, plus the gaps between them, plus the
 * card's own horizontal padding.
 */
const gridMin = computed(() => {
  const tracks = props.columns.length + (props.leadColumn ? 1 : 0) + (props.actionWidth ? 1 : 0);
  const columnFloor = props.columns.reduce((total, column) => total + trackMin(column.width), 0);
  const lead = props.leadColumn ? props.leadWidth : 0;
  return `${columnFloor + props.actionWidth + lead + gap * (tracks - 1) + framePadding}px`;
});

const rowStyle = computed(() => ({
  gridTemplateColumns: grid.value,
  minWidth: gridMin.value,
}));

const cardBorder = gaiaNeutral.borderCard;
const headBackground = gaiaNeutral.surfaceSoft;
const headBorder = gaiaNeutral.borderDivider;
const headColor = gaiaNeutral.textSubtle;
const sortedColor = gaiaNeutral.ink;
const hoverColor = gaiaNeutral.text;
const headSize = gaiaFontSize.micro;
const headWeight = String(gaiaFontWeight.bold);
const headTracking = gaiaTracking.label;
const surface = gaiaNeutral.surface;

// The caret is drawn as a path rather than an icon so the arrow's stem length
// matches the mock's 10px box exactly.
const caret = computed(() =>
  props.sortDirection === 'asc' ? 'M5 8.5v-7M2.5 4 5 1.5 7.5 4' : 'M5 1.5v7M2.5 6 5 8.5 7.5 6'
);

function isSorted(column: GaiaDataColumn): boolean {
  return Boolean(column.sortable) && props.sortActive && props.sortKey === column.key;
}
</script>

<template>
  <!-- The entity list's data card: a CSS-grid table rather than n-data-table,
       because every column is a named grid track (`minmax(180px,1.1fr)`) shared
       by the header, the rows, the skeletons and the row-state strips — naive's
       table owns its own layout and can't hand that template out. The mock's
       own handoff note asks for exactly this component ("GaiaDataTable"). -->
  <div class="gaia-data-table">
    <!-- A card-wide notice above the header — the stale-data banner on the
         views list. It sits outside the scroller for the same reason the footer
         does: it belongs to the card, not to the columns, so it must not slide
         away when the table is scrolled sideways. -->
    <div v-if="$slots.banner" class="banner">
      <slot name="banner" />
    </div>

    <!-- Only the header and the rows scroll sideways. The footer is a sibling
         of the scroller, not a child, so pagination stays put at the left of
         the card however far the columns are scrolled. -->
    <div class="scroll">
      <div class="head" :style="rowStyle">
        <div v-if="props.leadColumn" class="head-lead"><slot name="head-lead" /></div>
        <div
          v-for="column in props.columns"
          :key="column.key"
          class="head-cell"
          :class="{
            sortable: column.sortable,
            sorted: isSorted(column),
            right: column.align === 'right',
            center: column.align === 'center',
          }"
          @click="column.sortable && emit('sort', column.key)"
        >
          {{ column.label }}
          <svg
            v-if="isSorted(column)"
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          >
            <path :d="caret" />
          </svg>
        </div>
        <div v-if="props.actionWidth" />
      </div>

      <slot :row-style="rowStyle" />
    </div>

    <div v-if="$slots.footer" class="foot">
      <slot name="footer" />
    </div>
  </div>
</template>

<style scoped>
/* The card itself never scrolls — it only clips, so the rounded corners cut the
   header and footer strips. `hidden` is safe here: every menu inside a row is
   teleported to the body, so nothing needs to escape the card. */
.gaia-data-table {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid v-bind(cardBorder);
  border-radius: 10px;
  background: v-bind(surface);
}

/* Header and rows scroll together as one block, so a header cell can never
   drift out of line with the column under it. */
.scroll {
  display: flex;
  flex-direction: column;
  overflow-x: auto;
}

.head {
  display: grid;
  align-items: center;
  gap: 16px;
  height: 40px;
  padding: 0 18px;
  background: v-bind(headBackground);
  border-bottom: 1px solid v-bind(headBorder);
  font-size: v-bind(headSize);
  font-weight: v-bind(headWeight);
  letter-spacing: v-bind(headTracking);
  text-transform: uppercase;
  color: v-bind(headColor);
}

.head-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.head-cell.right {
  justify-content: flex-end;
}

.head-cell.center {
  justify-content: center;
}

.head-cell.sortable {
  cursor: pointer;
}

.head-cell.sortable:hover {
  color: v-bind(hoverColor);
}

.head-cell.sorted {
  color: v-bind(sortedColor);
}

.foot {
  border-top: 1px solid v-bind(headBorder);
  background: v-bind(headBackground);
  border-radius: 0 0 10px 10px;
}

/* The lead track holds a control, not a label, so it centres like a body cell
   rather than sitting on the header's caps baseline. */
.head-lead {
  display: flex;
  align-items: center;
}

/* The banner supplies its own surface and border; the card only rounds the top
   corners it now occupies so the tint doesn't square them off. */
.banner :deep(.n-alert) {
  border-radius: 10px 10px 0 0;
}
</style>
